import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { IGiftCardType, IGiftCardListResponse, featureName, GetGiftCardListParams, IGiftCardSummary } from './constants';
import { ISearchCustomerItem } from 'modules/customer/widgets/AddCustomer/type';

const createAsync = geneAsyncAction(featureName);

const async = {
  getGiftCardList: createAsync<GetGiftCardListParams, IGiftCardListResponse>('getGiftCardList'),
  getGiftCardDetail: createAsync<string, IGiftCardType>('getGiftCardDetail'),
  getGiftCardSummary: createAsync<void, IGiftCardSummary>('getGiftCardSummary'),
  getInitialCustomers: createAsync('getInitialCustomers'),
};

export type IGiftCardReducerState = {
  isLoading: boolean;
  giftCardParams: GetGiftCardListParams,
  giftCardData: IGiftCardListResponse | null;
  selectedGiftCard: IGiftCardType | null;
  giftCardSummary: IGiftCardSummary | null;
  initialSearchCustomers: ISearchCustomerItem[];
};

type MyState = IGiftCardReducerState;

const getCurrentState = (state: RootState): MyState => state[featureName];

const selector = <T = MyState>(key: keyof T, defaultValue?: any) =>
  useAppSelector((state) => get(getCurrentState(state), key, defaultValue));

const selectors = {
  isLoading: () => selector('isLoading') as MyState['isLoading'],
  giftCardData: () => selector('giftCardData') as MyState['giftCardData'],
  selectedGiftCard: () => selector('selectedGiftCard') as MyState['selectedGiftCard'],
  giftCardSummary: () => selector('giftCardSummary') as MyState['giftCardSummary'],
  initialSearchCustomers: () => selector('initialSearchCustomers') as MyState['initialSearchCustomers'],
  giftCardParams: () => selector('giftCardParams') as MyState['giftCardParams'],
  getGiftCardParams: (state: RootState) => getCurrentState(state).giftCardParams as MyState['giftCardParams'],
};

const Slice = createSlice({
  name: featureName,
  initialState: {
    isLoading: false,
    giftCardData: null,
    selectedGiftCard: null,
    giftCardSummary: null,
    giftCardParams: {
      page: 1, size: 10,
    },
  } as MyState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(async.getInitialCustomers.fetch, (state) => {
        state.initialSearchCustomers = [];
      })
      .addCase(async.getInitialCustomers.success, (state, { payload }) => {
        state.initialSearchCustomers = payload;
      })
      .addCase(async.getInitialCustomers.fail, (state) => {
        state.initialSearchCustomers = [];
      })

      .addCase(async.getGiftCardList.fetch, (state, { payload }) => {
        state.isLoading = true;
        const _params = { ...state.giftCardParams, ...payload };
        if (!_params.keySearch || _params.keySearch == '') {
          delete _params.keySearch;
        }
        state.giftCardParams = _params;
      })
      .addCase(async.getGiftCardList.success, (state, { payload }) => {
        state.giftCardData = payload;
        state.isLoading = false;
      })
      .addCase(async.getGiftCardList.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(async.getGiftCardDetail.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.getGiftCardDetail.success, (state, { payload }) => {
        state.selectedGiftCard = payload;
        state.isLoading = false;
      })
      .addCase(async.getGiftCardDetail.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(async.getGiftCardSummary.success, (state, { payload }) => {
        state.giftCardSummary = payload;
      });
  },
});

const actions = {
  ...Slice.actions,
  async,
};

const reducer = Slice.reducer;
export const giftCardActions = actions;
export { actions, reducer, selectors }; 